import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/services/api';
import { formatDateTime, toRomanNumeral } from '@/lib/utils';
import type { RecordItem, CriminalCase } from '@/types';

interface NeedleViewProps {
  prisonerId: string;
}

export function NeedleView({ prisonerId }: NeedleViewProps) {
  const [selectedBand, setSelectedBand] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['needle-view', prisonerId],
    queryFn: () => api.getNeedleView(prisonerId),
  });

  if (isLoading) {
    return <p className="text-muted-foreground">Lädt...</p>;
  }

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      {/* Nadel 1 - Grundunterlagen */}
      <Card className="border-blue-500/50">
        <CardHeader className="bg-blue-500/10">
          <CardTitle className="flex items-center gap-2 text-base">
            <div className="h-3 w-3 rounded-full bg-blue-500" />
            Nadel 1 - Grundunterlagen
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {!data?.needle1.length ? (
            <p className="text-sm text-muted-foreground">Keine Dokumente.</p>
          ) : (
            <div className="space-y-2">
              {data.needle1.map((item: RecordItem) => (
                <div
                  key={item.id}
                  className="rounded border p-2 text-sm hover:bg-muted/50"
                >
                  <p className="font-medium">{item.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {item.needle1DocType}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Nadel 2 - Vollstreckungsunterlagen */}
      <Card className="border-green-500/50">
        <CardHeader className="bg-green-500/10">
          <CardTitle className="flex items-center gap-2 text-base">
            <div className="h-3 w-3 rounded-full bg-green-500" />
            Nadel 2 - Vollstreckung
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {!data?.needle2.length ? (
            <p className="text-sm text-muted-foreground">Keine Haftsachen.</p>
          ) : (
            <div className="space-y-4">
              {data.needle2.map(
                ({ case: criminalCase, items }: { case: CriminalCase; items: RecordItem[] }) => (
                  <div key={criminalCase.id}>
                    <h4 className="mb-2 font-medium">
                      Haftsache {criminalCase.romanNumeral}
                      {criminalCase.caseReference && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          ({criminalCase.caseReference})
                        </span>
                      )}
                    </h4>
                    <div className="space-y-1 pl-4 border-l-2 border-green-500/30">
                      {items.map((item: RecordItem) => (
                        <div
                          key={item.id}
                          className="rounded border p-2 text-sm hover:bg-muted/50"
                        >
                          <span className="font-mono text-xs text-green-600">
                            {criminalCase.romanNumeral}/{item.caseSequenceNumber}
                          </span>
                          <p className="font-medium">{item.title}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Nadel 3 - Sonstige Schriftstücke */}
      <Card className="border-orange-500/50">
        <CardHeader className="bg-orange-500/10">
          <CardTitle className="flex items-center gap-2 text-base">
            <div className="h-3 w-3 rounded-full bg-orange-500" />
            Nadel 3 - Sonstige
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {/* Band Tabs */}
          {data?.needle3 && data.needle3.length > 1 && (
            <div className="mb-4 flex gap-1">
              {data.needle3.map(({ band }: { band: number }) => (
                <Button
                  key={band}
                  variant={selectedBand === band ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setSelectedBand(band)}
                >
                  Band {band}
                </Button>
              ))}
            </div>
          )}

          {!data?.needle3.length ? (
            <p className="text-sm text-muted-foreground">Keine Dokumente.</p>
          ) : (
            <div className="space-y-2">
              {data.needle3
                .find((b: { band: number }) => b.band === selectedBand)
                ?.items.map((item: RecordItem) => (
                  <div
                    key={item.id}
                    className="rounded border p-2 text-sm hover:bg-muted/50"
                  >
                    <span className="font-mono text-xs text-orange-600">
                      Bl. {item.folioNumber}
                    </span>
                    <p className="font-medium">{item.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatDateTime(item.createdAt)}
                    </p>
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
